/**
 * Local app PIN (F4) — 4-6 digits, ONLY on this device.
 *
 * Security: SHA-256(pin + random salt) is stored in localStorage; the plaintext
 * PIN is never persisted or sent to the server. This is a privacy barrier
 * (against snoopers), not data encryption: the local SQLite is not encrypted.
 *
 * Rate limit: 5 failed attempts → 30 s wait (persisted, survives page
 * reloads).
 *
 * ── Extension point: native biometrics ──────────────────────────────────────
 * In Tauri (desktop/mobile), biometric unlock can be added before the
 * keypad using a plugin (e.g. tauri-plugin-biometric): if
 * `isTauriRuntime()` and biometrics validate, call the same success flow
 * as `verifyPin` (resetAttempts + unlock). Without new dependencies in this
 * wave, it is out of scope.
 */

const PIN_STORAGE_KEY = "pin.v1";
const PIN_LOCK_STORAGE_KEY = "pin.lock.v1";

export const PIN_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 6;
export const PIN_MAX_ATTEMPTS = 5;
export const PIN_LOCKOUT_MS = 30_000;

const SALT_BYTES = 16;

interface StoredPin {
  saltHex: string;
  hashHex: string;
}

export interface PinAttemptState {
  failures: number;
  /** Epoch ms until the keypad is locked, or null. */
  lockUntil: number | null;
}

function getStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function bytesToHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(input: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return bytesToHex(new Uint8Array(digest));
}

function readStoredPin(): StoredPin | null {
  const raw = getStorage()?.getItem(PIN_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    const saltHex: unknown = Reflect.get(parsed, "saltHex");
    const hashHex: unknown = Reflect.get(parsed, "hashHex");
    if (typeof saltHex !== "string" || typeof hashHex !== "string") {
      return null;
    }
    return { saltHex, hashHex };
  } catch {
    return null;
  }
}

/** Valid format: 4-6 digits. */
export function isValidPinFormat(pin: string): boolean {
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
  return new RegExp(`^\\d{${PIN_MIN_LENGTH},${PIN_MAX_LENGTH}}$`).test(pin);
}

export function hasPinConfigured(): boolean {
  return readStoredPin() !== null;
}

/** Stores SHA-256(pin + salt) with a new random salt. */
export async function savePin(pin: string): Promise<void> {
  if (!isValidPinFormat(pin)) {
    throw new Error("PIN must be 4-6 digits");
  }
  const salt = new Uint8Array(SALT_BYTES);
  crypto.getRandomValues(salt);
  const saltHex = bytesToHex(salt);
  const hashHex = await sha256Hex(pin + saltHex);
  getStorage()?.setItem(PIN_STORAGE_KEY, JSON.stringify({ saltHex, hashHex }));
  resetPinAttempts();
}

export function clearPin(): void {
  getStorage()?.removeItem(PIN_STORAGE_KEY);
  resetPinAttempts();
}

/** Compares against the stored hash. False if no PIN is configured. */
export async function verifyPin(pin: string): Promise<boolean> {
  const stored = readStoredPin();
  if (!stored) {
    return false;
  }
  const hashHex = await sha256Hex(pin + stored.saltHex);
  return hashHex === stored.hashHex;
}

// ─── Attempts / lockout ───────────────────────────────────────────────────────

export function readPinAttempts(): PinAttemptState {
  const raw = getStorage()?.getItem(PIN_LOCK_STORAGE_KEY);
  if (!raw) {
    return { failures: 0, lockUntil: null };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return { failures: 0, lockUntil: null };
    }
    const failures: unknown = Reflect.get(parsed, "failures");
    const lockUntil: unknown = Reflect.get(parsed, "lockUntil");
    return {
      failures: typeof failures === "number" ? failures : 0,
      lockUntil: typeof lockUntil === "number" ? lockUntil : null,
    };
  } catch {
    return { failures: 0, lockUntil: null };
  }
}

function writePinAttempts(state: PinAttemptState): void {
  try {
    getStorage()?.setItem(PIN_LOCK_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Not fatal: without persistence, lockout only lasts for the page session.
  }
}

/** Records a failure; reaching PIN_MAX_ATTEMPTS starts the 30 s wait. */
export function recordFailedPinAttempt(now: number = Date.now()): PinAttemptState {
  const current = readPinAttempts();
  const failures = current.failures + 1;
  const next: PinAttemptState =
    failures >= PIN_MAX_ATTEMPTS
      ? { failures: 0, lockUntil: now + PIN_LOCKOUT_MS }
      : { failures, lockUntil: current.lockUntil };
  writePinAttempts(next);
  return next;
}

export function resetPinAttempts(): void {
  getStorage()?.removeItem(PIN_LOCK_STORAGE_KEY);
}

/** Remaining lockout milliseconds (0 if no lockout is active). */
export function pinLockRemainingMs(now: number = Date.now()): number {
  const { lockUntil } = readPinAttempts();
  if (lockUntil === null || lockUntil <= now) {
    return 0;
  }
  return lockUntil - now;
}
